import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Typography, Container, Grid, Link } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getCanonicalUrl } from '../utils/canonicalUrl';
import TeamMemberCard from '../components/TeamMemberCard';

export default function About() {
  const team = [
    {
      name: 'Fraser Robinson',
      role: 'Founder & Principal Consultant',
      bio: 'Fraser is a backend engineer and AI practitioner with deep expertise in large-scale AI systems and cloud infrastructure. As Head of AI at Crescendo LTD he architects distributed platforms and leads an international team. He previously spearheaded enterprise NLP and generative AI solutions as VP Engineering at CiiVSOFT. Fraser holds a Distinction in Aerospace Engineering from Kingston University and leverages this diverse background to deliver pragmatic, high-ROI solutions.',
      imageSrc: '/assets/visuals/FraserRobinson.jpeg',
    },
  ];

  const location = useLocation();
  const canonicalUrl = getCanonicalUrl(location.pathname);

  return (
    <Box sx={{ color: 'common.white' }}>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <title>About Robinson Engineering</title>
        <meta name="description" content="Learn about our mission, values and engineering leadership." />
        <meta property="og:title" content="About Robinson Engineering" />
        <meta property="og:description" content="Discover our story, mission and founder background." />
        <meta
          property="og:image"
          content="/assets/visuals/re-profile-blue-ai-hud.webp"
        />
      </Helmet>
      <Box
        sx={{
          position: 'relative',
          backgroundImage:
            'linear-gradient(rgba(24,45,70,0.8), rgba(53,85,111,0.8)), url("/assets/visuals/re-profile-blue-ai-hud.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          textAlign: 'center',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container>
          <Typography variant="h2" component="h1" gutterBottom>
            About Us
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            At the intersection of deep engineering and pragmatic AI.
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: 6, backgroundColor: 'primary.main' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Our Story
        </Typography>
        <Typography paragraph>
          Fraser's fascination with engineering began in childhood when he assembled a makeshift rocket in his garden and watched it leap skyward.
        </Typography>
        <Typography paragraph>
          After graduating in Aerospace Engineering he shifted from hardware to code, applying the same rigor to emerging fields of artificial intelligence.
        </Typography>
        <Typography paragraph>
          In 2024 he founded Robinson Engineering on the belief that rapid, hands-on experimentation delivers the most reliable solutions.
        </Typography>
        <Typography paragraph>
          That ethos also champions balance&mdash;work hard, then close the laptop and enjoy the trail, the run or time with family.
        </Typography>
        <Typography paragraph>
          Today our mission is to help organisations harness pragmatic AI that yields measurable ROI while keeping people at the heart of every system.
        </Typography>
      </Container>

      <Container sx={{ py: 6, backgroundColor: 'primary.dark' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Our Approach
        </Typography>
        <Typography paragraph>
          We tackle each engagement with a focus on rapid experimentation, clear communication and measurable outcomes.
        </Typography>
      </Container>

      <Container sx={{ py: 6, backgroundColor: 'primary.main' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Our Mission & Values
        </Typography>
        <Typography paragraph>
          Our mission is to drive breakthrough outcomes through relentless curiosity and exploration of AI.
        </Typography>
        <Typography variant="h6" component="h3" gutterBottom>
          Our Core Values
        </Typography>
        <Box component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1">
              We are pragmatists – always solving business problems, not academic ones.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Client-first approach – no stupid questions, we build confidence and comfort.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              We are educators – our solutions are understandable and explainable.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Fast &amp; iterative – we ship value quickly, perfectionism kills progress.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Curiosity is our driver – constant exploration fuels innovation.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              We seek hard problems – difficult challenges yield high-impact results.
            </Typography>
          </li>
        </Box>
      </Container>

      <Container sx={{ py: 6, backgroundColor: 'primary.dark', textAlign: 'center' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Meet the Founder
        </Typography>
        <Typography paragraph sx={{ maxWidth: 800 }}>
          Fraser is a backend engineer and AI practitioner with deep expertise in large-scale AI systems and cloud infrastructure. As Head of AI at Crescendo LTD he architects distributed platforms and leads an international team. He previously spearheaded enterprise NLP and generative AI solutions as VP Engineering at CiiVSOFT. Fraser holds a Distinction in Aerospace Engineering from Kingston University and leverages this diverse background to deliver pragmatic, high-ROI solutions.{' '}
          <Link
            href="https://www.linkedin.com/in/fraserrobinson/"
            target="_blank"
            rel="noopener"
            color="secondary.light"
          >
            Connect with Fraser on LinkedIn
          </Link>
          .
        </Typography>
        <Typography sx={{ fontStyle: 'italic', mt: 2 }}>
          “AI isn’t magic—it’s engineering.”
        </Typography>
        <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
          {team.map((member) => (
            <Grid
              item
              xs={12}
              md={4}
              key={member.name}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <TeamMemberCard {...member} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
